import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Store, 
  Bot, 
  BookOpen, 
  Activity, 
  Sparkles, 
  ShieldCheck, 
  ArrowRight, 
  CheckCircle2, 
  FileText, 
  Upload, 
  ExternalLink,
  Layers,
  Database,
  Cpu,
  Lock,
  ChevronRight,
  Handshake
} from 'lucide-react';
import { 
  EXECUTIVE_TABS, 
  HUB_PARTNER_ONLY,
  TOTAL_SKU_COUNT, 
  TENANTS_COUNT, 
  ACTIVE_TENANTS,
  TOTAL_MASTER_DOCS, 
  TOTAL_PAGES, 
  TOTAL_CHUNKS,
  TOTAL_SKU_LINKS,
  ALWIS_CORE_CAPABILITIES,
  SECURITY_COMPLIANCE,
  PRICE_CATALOG_SYNC,
  SYSTEM_HEALTH_MATRIX,
  INITIAL_CHANGELOG_EVENTS
} from './executive-data.js';
import { ExecutiveHeader } from './ExecutiveHeader.jsx';
import { ExecutiveKpiStrip } from './ExecutiveKpiStrip.jsx';
import { ExecutivePillars } from './ExecutivePillars.jsx';
import { ShopPillarCard } from './pillars/ShopPillarCard.jsx';
import { AlwisPillarCard } from './pillars/AlwisPillarCard.jsx';
import { AlwisQualityCard } from './AlwisQualityCard.jsx';
import { RagPillarCard } from './pillars/RagPillarCard.jsx';
import { RagUploadCenter } from './upload/RagUploadCenter.jsx';
import { ChangelogFeed } from './changelog/ChangelogFeed.jsx';
import { SystemHealthStatus } from './health/SystemHealthStatus.jsx';
import { PartnerProgramView } from './partner/PartnerProgramView.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { cn } from '../../lib/utils.js';
import { loginSession, getSession } from '../../lib/api.js';

const TAB_ICON_MAP = {
  overview: LayoutDashboard,
  partner: Handshake,
  shop: Store,
  alwis: Bot,
  rag: BookOpen,
  changelog: Activity,
};

export function ExecutiveHubView({
  route,
  onNavigate,
  onOpenCopilot,
  isUnlocked: propIsUnlocked,
  partnerOnly = HUB_PARTNER_ONLY,
}) {
  // Determine active tab from route params or fallback to 'overview'
  const routeTab = route?.tab || route?.params?.tab || 'overview';
  const [activeTabState, setActiveTab] = useState(routeTab);
  // Partner-only: Startseite ist das Partnerprogramm, übrige Tabs sind ausgeblendet.
  const visibleTabs = partnerOnly ? EXECUTIVE_TABS.filter((t) => t.id === 'partner') : EXECUTIVE_TABS;
  const activeTab = visibleTabs.some((t) => t.id === activeTabState) ? activeTabState : visibleTabs[0].id;

  // Dynamic live state updated by uploads
  const [liveEvents, setLiveEvents] = useState(INITIAL_CHANGELOG_EVENTS);
  const [docCount, setDocCount] = useState(TOTAL_MASTER_DOCS);
  const [chunkCount, setChunkCount] = useState(TOTAL_CHUNKS);

  // Auth & Session State
  const [isUnlocked, setIsUnlocked] = useState(
    propIsUnlocked ?? (typeof document === 'undefined')
  );
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inspect active session on mount
  useEffect(() => {
    if (propIsUnlocked !== undefined) {
      setIsUnlocked(propIsUnlocked);
      return;
    }
    let isMounted = true;
    getSession()
      .then((res) => {
        if (isMounted && res?.authenticated && (res.role === 'hub' || res.role === 'admin')) {
          setIsUnlocked(true);
        }
      })
      .catch(() => {
        // Fail-closed: stay locked
      });
    return () => {
      isMounted = false;
    };
  }, [propIsUnlocked]);

  useEffect(() => {
    if (routeTab && routeTab !== activeTabState) {
      setActiveTab(routeTab);
    }
  }, [routeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (onNavigate) {
      const targetUrl = tabId === 'overview' ? '/hub' : `/hub/${tabId}`;
      onNavigate(targetUrl);
    }
  };

  const handleOpenCopilot = (prompt) => {
    if (onOpenCopilot) {
      onOpenCopilot(prompt || 'Hallo ALWIS, bitte zeige mir den aktuellen Planungs- und Systemstatus.');
    }
  };

  const handleUploadComplete = (uploadedItem) => {
    // Truth-enforced: No fake counter increments or synthetic ingestion events without backend pipeline
    if (!uploadedItem) return;
  };

  const handleUnlock = async (e) => {
    e?.preventDefault();
    if (!pinInput.trim()) {
      setPinError('Bitte PIN eingeben.');
      return;
    }
    setIsSubmitting(true);
    setPinError('');
    try {
      const res = await loginSession('hub', pinInput.trim());
      if (res?.ok && res?.authenticated) {
        setIsUnlocked(true);
        setPinInput('');
        setPinError('');
      } else {
        setPinError('Ungültiger PIN. Bitte erneut versuchen.');
      }
    } catch (err) {
      if (err.status === 401) {
        setPinError('Ungültiger PIN. Bitte erneut versuchen.');
      } else if (err.status === 503) {
        setPinError('Authentifizierungsserver nicht konfiguriert (HUB_ACCESS_SECRET fehlt).');
      } else {
        setPinError(err.message || 'Authentifizierungsfehler');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isUnlocked) {
    return (
      <div 
        data-testid="executive-hub-auth-gate"
        className="min-h-screen bg-[var(--background,#f8fafc)] flex items-center justify-center p-4"
      >
        <div className="w-full max-w-md rounded-2xl border border-[var(--border)] bg-[var(--surface,white)] p-8 shadow-md space-y-6 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary,#e3000b)]/10 text-[var(--primary,#e3000b)] border border-[var(--primary,#e3000b)]/20 shadow-2xs">
            <Lock className="h-7 w-7" aria-hidden="true" />
          </div>
          <div>
            <span className="inline-flex items-center gap-1 rounded-md bg-[var(--primary,#e3000b)] px-2.5 py-0.5 text-[11px] font-black uppercase text-white tracking-wider">
              ASL Ademco
            </span>
            <h1 className="font-display text-xl font-bold text-[var(--foreground)] mt-2">
              {partnerOnly ? 'ASL Partnerprogramm' : 'Betriebsleiter Executive Portal'}
            </h1>
            <p className="text-xs text-[var(--secondary)] mt-1">
              {partnerOnly ? 'Geschützter Bereich. Bitte Zugangspasswort eingeben:' : 'Geschützter Management-Bereich. Bitte Autorisierungs-PIN eingeben:'}
            </p>
          </div>

          <form onSubmit={handleUnlock} noValidate aria-label="PIN-Autorisierung Betriebsleiter-Portal" className="space-y-4">
            <div>
              <label htmlFor="hub-pin-input" className="sr-only">
                {partnerOnly ? 'Zugangspasswort' : 'Autorisierungs-PIN'}
              </label>
              <input
                id="hub-pin-input"
                type="password"
                autoComplete="current-password"
                value={pinInput}
                onChange={(e) => {
                  setPinInput(e.target.value);
                  if (pinError) setPinError('');
                }}
                placeholder={partnerOnly ? 'Passwort eingeben...' : 'PIN eingeben...'}
                aria-required="true"
                aria-invalid={Boolean(pinError)}
                aria-describedby={pinError ? "hub-pin-error" : undefined}
                className={cn(
                  'w-full text-center tracking-widest font-mono text-base rounded-xl border min-h-[44px] px-4 py-3 outline-none transition-all',
                  pinError
                    ? 'border-red-500 bg-red-50 text-red-900 focus:ring-2 focus:ring-red-200'
                    : 'border-[var(--border)] bg-white text-[var(--foreground)] focus:border-[var(--primary,#e3000b)] focus:ring-2 focus:ring-[var(--ring)]'
                )}
                autoFocus
              />
            </div>
            {pinError && (
              <p id="hub-pin-error" role="alert" aria-live="assertive" className="text-xs font-semibold text-red-600">
                {pinError}
              </p>
            )}
            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={isSubmitting}
              className="w-full h-11 min-h-[44px] bg-[var(--primary,#e3000b)] text-white font-bold cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--ring,var(--primary))]"
            >
              {isSubmitting ? 'Prüfe Zugang...' : (partnerOnly ? 'Anmelden' : 'Dashboard entsperren')}
            </Button>
          </form>

          <div className="pt-2 border-t border-[var(--border)] text-[11px] text-[var(--secondary)]">
            <span>{partnerOnly ? 'ASL Ademco • Partnerprogramm' : 'Interne Vorschau • AMB Digital / ASL Ademco'}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="executive-hub-view"
      className="min-h-screen bg-[var(--background,#f8fafc)] pb-20 pt-6"
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Top Executive Header */}
        {activeTab !== 'partner' && (<>
        <ExecutiveHeader
          onNavigate={onNavigate}
          onOpenCopilot={() => handleOpenCopilot('Hallo ALWIS, bitte zeige mir den aktuellen Planungs- und Systemstatus.')}
          lastSyncDate={PRICE_CATALOG_SYNC.formattedDate}
        />

        {/* Prominent Demo Mode Banner */}
        <div data-testid="executive-demo-banner" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-xs text-amber-900 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-200 text-amber-950 font-black text-xs shrink-0">!</span>
            <div>
              <span className="font-bold">Demo- &amp; Vorschau-Modus:</span>
              <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                Alle angezeigten Metriken, Katalogzahlen (1.472 SKUs) und Health-Status basieren auf exemplarischen Musterdaten. Keine produktive Live-Verbindung oder persistierte Zertifizierung.
              </p>
            </div>
          </div>
          <span className="self-start sm:self-auto inline-flex items-center rounded-md bg-amber-200/80 px-2.5 py-1 text-[10px] font-mono font-bold uppercase text-amber-950">
            DEMO-MODE
          </span>
        </div>

        {/* 4-KPI Overview Strip */}
        <ExecutiveKpiStrip 
          onKpiClick={(kpi) => {
            if (kpi.id === 'skus') handleTabChange('shop');
            else if (kpi.id === 'tenants') handleTabChange('shop');
            else if (kpi.id === 'rag-corpus') handleTabChange('rag');
            else if (kpi.id === 'compliance') handleTabChange('alwis');
          }}
        />
        </>)}

        {/* Tab Switcher Bar */}
        {visibleTabs.length > 1 && (
        <div className="flex border border-slate-200 bg-white rounded-xl p-1.5 shadow-xs overflow-x-auto">
          <nav className="flex items-center gap-1.5 min-w-max" aria-label="Betriebsleiter Hub Tabs">
            {visibleTabs.map((tab) => {
              const Icon = TAB_ICON_MAP[tab.id] || LayoutDashboard;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  data-testid={`hub-tab-${tab.id}`}
                  onClick={() => handleTabChange(tab.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-4 py-2.5 min-h-[40px] text-xs sm:text-sm font-bold transition-all cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]',
                    isActive
                      ? 'bg-[var(--primary,#e3000b)] text-white shadow-xs'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  )}
                >
                  <Icon className={cn('h-4 w-4', isActive ? 'text-white' : 'text-[var(--primary,#e3000b)]')} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        )}

        {/* Tab 0: PARTNERPROGRAMM (Manges-Überblick, serverseitige Daten) */}
        {activeTab === 'partner' && (
          <div className="animate-fadeIn" data-testid="tab-panel-partner">
            <PartnerProgramView />
          </div>
        )}

        {/* Tab 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-8 animate-fadeIn" data-testid="tab-panel-overview">
            {/* System Health Matrix Indicator */}
            <SystemHealthStatus />

            {/* The 3 Main Platform Pillars Grid */}
            <ExecutivePillars
              onNavigateTab={handleTabChange}
              onOpenCopilot={handleOpenCopilot}
              onOpenUpload={() => handleTabChange('rag')}
              onNavigate={onNavigate}
              docCount={docCount}
              chunkCount={chunkCount}
            />

            {/* Quick Action Matrix Banner */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[var(--primary,#e3000b)]" />
                    <h4 className="font-display text-sm font-bold text-slate-900">
                      Sicherheit &amp; Datenschutz-Architektur (Konzept)
                    </h4>
                  </div>
                  <p className="text-xs text-slate-600">
                    Systemarchitektur orientiert an Vorgaben der DSGVO, DIN 14675 und VdS 2311 (Konzeptuelle Vorbereitung, Audit ausstehend).
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-xs py-1 px-2.5 font-bold">
                    Zero Audio Retention (Konzept)
                  </Badge>
                  <Badge variant="outline" className="bg-slate-100 text-slate-800 border-slate-300 text-xs py-1 px-2.5 font-mono font-bold">
                    EU Edge (Vorschau)
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: WHITE-LABEL SHOP */}
        {activeTab === 'shop' && (
          <div className="space-y-6 animate-fadeIn" data-testid="tab-panel-shop">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xs space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <div className="flex items-center gap-2">
                    <Store className="h-5 w-5 text-[var(--primary,#e3000b)]" />
                    <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900">
                      Säule 1: White-Label Partner-Shop &amp; D1 Katalog
                    </h2>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Übersicht des D1-Artikelkatalogs, Mandanten-Instanzen und automatisierter Preislisten-Synchronisation.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onNavigate && onNavigate('/')}
                    className="text-xs font-bold gap-1.5 border-slate-300 focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
                  >
                    <Store className="h-3.5 w-3.5 text-[var(--primary,#e3000b)]" />
                    <span>Storefront öffnen</span>
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => onNavigate && onNavigate('/admin')}
                    className="text-xs font-bold gap-1.5 bg-[var(--primary,#e3000b)] text-white focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
                  >
                    <Layers className="h-3.5 w-3.5" />
                    <span>Control-Plane Admin</span>
                  </Button>
                </div>
              </div>

              {/* Detailed Shop Pillar Card */}
              <ShopPillarCard
                isDetailed={true}
                skuCount={TOTAL_SKU_COUNT}
                tenants={ACTIVE_TENANTS}
                catalogTimestamp={PRICE_CATALOG_SYNC.formattedDate}
                onNavigate={onNavigate}
                onOpenTab={handleTabChange}
              />

              {/* Grid with Shop Metrics & Tenant Matrix */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <span className="text-xs text-slate-500 font-medium">Artikel im Großhandel (Muster)</span>
                  <p className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-1">{TOTAL_SKU_COUNT}</p>
                  <p className="text-[11px] text-amber-800 font-semibold mt-1">Musterkatalog (Demo)</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <span className="text-xs text-slate-500 font-medium">Mandanten-Partitionen (Muster)</span>
                  <p className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-1">{TENANTS_COUNT}</p>
                  <p className="text-[11px] text-slate-600 font-medium mt-1">Muster-Mandanten (Demo)</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <span className="text-xs text-slate-500 font-medium">Preiskatalog-Synchronisation</span>
                  <p className="font-display text-base font-bold text-slate-900 tabular-nums mt-1 font-mono">{PRICE_CATALOG_SYNC.formattedDate}</p>
                  <p className="text-[11px] text-slate-600 font-medium mt-1">Demo-Modus (Nicht synchronisiert)</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  <span className="text-xs text-slate-500 font-medium">Kategorie-Taxonomie</span>
                  <p className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-1">6 / 38</p>
                  <p className="text-[11px] text-slate-600 font-medium mt-1">6 Haupt- / 38 Unterkategorien</p>
                </div>
              </div>

              {/* Tenant Status Table */}
              <div className="space-y-3 pt-2">
                <h3 className="font-display text-sm font-bold text-slate-900">
                  Mandanten-Shops im Verbund (Demo-Vorschau)
                </h3>
                <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white overflow-hidden text-xs">
                  {ACTIVE_TENANTS.map((tName, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3.5 hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 font-bold text-slate-700">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">{tName}</p>
                          <p className="text-[11px] text-slate-500">Individuelle B2B-Konditionen &amp; CI-Branding (Vorschau)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 font-semibold">
                          Demo-Muster
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 3: ALWIS FACHBERATER */}
        {activeTab === 'alwis' && (
          <div className="space-y-6 animate-fadeIn" data-testid="tab-panel-alwis">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xs space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200">
                <div>
                  <div className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-[var(--primary,#e3000b)]" />
                    <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900">
                      Säule 2: ALWIS KI-Produkt- &amp; Sortimentsberater (Demo)
                    </h2>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Technischer Assistent für Produktauswahl, Zubehör-Kompatibilität, Datenblatt-Auskünfte und 1-Klick-Stücklisten.
                  </p>
                </div>

                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleOpenCopilot('Welches Zubehör und Netzteil passt zu einer SATEL Zentrale?')}
                  className="text-xs font-bold gap-1.5 bg-[var(--primary,#e3000b)] text-white focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>ALWIS Copilot öffnen</span>
                </Button>
              </div>

              {/* Detailed ALWIS Pillar Card */}
              <AlwisPillarCard
                isDetailed={true}
                capabilities={ALWIS_CORE_CAPABILITIES}
                zeroAudioRetention={SECURITY_COMPLIANCE.zeroAudioRetention}
                onOpenCopilot={handleOpenCopilot}
                onOpenTab={handleTabChange}
              />

              {/* Core Capabilities Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                {ALWIS_CORE_CAPABILITIES.map((cap, idx) => (
                  <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-[var(--primary,#e3000b)] bg-red-50 px-2 py-0.5 rounded border border-red-100">
                        {cap.code}
                      </span>
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </div>
                    <h4 className="font-display text-sm font-bold text-slate-900">{cap.name}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{cap.scope}</p>
                  </div>
                ))}
              </div>

              {/* Security & Privacy Banner */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-amber-700" />
                  <h4 className="font-display text-sm font-bold text-slate-900">
                    Architekturkonzept: Zero Audio Retention &amp; DSGVO-Orientierung
                  </h4>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">
                  Geplante WebRTC-Sprachübertragung ausschließlich flüchtig im Arbeitsspeicher des Inferenzknotens ohne Persistierung (Architekturkonzept, Audit ausstehend).
                </p>
              </div>

              {/* D1 ALWIS Chat Quality Telemetry Card */}
              <AlwisQualityCard />
            </div>
          </div>
        )}

        {/* Tab 4: RAG WISSENSDATENBANK */}
        {activeTab === 'rag' && (
          <div className="space-y-6 animate-fadeIn" data-testid="tab-panel-rag">
            {/* Detailed RAG Pillar Card */}
            <RagPillarCard
              isDetailed={true}
              docCount={docCount}
              pageCount={TOTAL_PAGES}
              chunkCount={chunkCount}
              skuLinkCount={TOTAL_SKU_LINKS}
              onOpenUpload={() => {}}
              onOpenTab={handleTabChange}
            />

            {/* Interactive Upload Center Component */}
            <div data-testid="rag-upload-center-slot">
              <RagUploadCenter
                onUploadComplete={handleUploadComplete}
              />
            </div>
          </div>
        )}

        {/* Tab 5: UPDATE & CHANGELOG FEED */}
        {activeTab === 'changelog' && (
          <div className="space-y-6 animate-fadeIn" data-testid="tab-panel-changelog">
            <ChangelogFeed
              events={liveEvents}
              onAddEvent={(newEvent) => setLiveEvents((prev) => [newEvent, ...prev])}
            />
          </div>
        )}

      </div>
    </div>
  );
}
